/**
 * The card menu of a chart in the Library (W2-10 projects-data; DESIGN.md §4.1): Open · New difficulty (shares the
 * song) · Duplicate · Export .pez · Version history · Rename · Download recovery data (charts whose last session
 * did not close) · Move to Trash, followed by the commands other modules place in the 'library.card' menu (run with
 * `{ id }` of the card).
 */

import type { AppServices, IconName } from '../../app/registry/types';
import type { MessageKey } from '../../i18n/types';
import type { ProjectSummary } from '../../persist/types';
import { m } from './library.messages';
import {
  downloadRecoveryData,
  duplicateProject,
  exportProject,
  openProject,
  showVersions,
  trashProject,
} from './libraryActions';
import { libraryStore } from './libraryStore';

export type CardAction =
  | {
      readonly kind: 'action';
      readonly id: string;
      readonly label: MessageKey;
      readonly icon: IconName;
      run(): Promise<unknown> | undefined;
    }
  | { readonly kind: 'command'; readonly command: string }
  | { readonly kind: 'separator' };

/** Ids of the Library's own entries (also used by tests). */
export type CardActionId =
  'open' | 'newDifficulty' | 'duplicate' | 'export' | 'versions' | 'rename' | 'recovery' | 'trash';

export function menuActionsFor(services: AppServices, p: ProjectSummary): CardAction[] {
  const store = libraryStore(services);
  const action = (
    id: CardActionId,
    label: MessageKey,
    icon: IconName,
    run: () => Promise<unknown> | undefined,
  ): CardAction => ({ kind: 'action', id, label, icon, run });
  const out: CardAction[] = [
    action('open', m.actOpen, 'FolderOpen', () => openProject(services, p)),
    action('newDifficulty', m.actNewDifficulty, 'CopyPlus', () => {
      store.update({ newDifficulty: p.id, focused: p.id });
      return undefined;
    }),
    action('duplicate', m.actDuplicate, 'Copy', () => duplicateProject(services, p)),
    action('export', m.actExport, 'PackageOpen', () => exportProject(services, p)),
    action('versions', m.actVersions, 'History', () => showVersions(services, p)),
    action('rename', m.actRename, 'Pencil', () => {
      store.update({ renaming: p.id, focused: p.id });
      return undefined;
    }),
  ];
  if (p.recoverable) out.push(action('recovery', m.actRecovery, 'LifeBuoy', () => downloadRecoveryData(services, p)));
  out.push({ kind: 'separator' });
  out.push(action('trash', m.actTrash, 'Trash2', () => trashProject(services, p)));
  const foreign = services.registry
    .menu('library.card')
    .flatMap((g) => g.commands)
    .filter((c) => !c.id.startsWith('project.'));
  if (foreign.length > 0) {
    out.push({ kind: 'separator' });
    for (const c of foreign) out.push({ kind: 'command', command: c.id });
  }
  return out;
}
